/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IHTTPError, IHTTPResponse, sendCommerceRequest, IHTTPRequestOptions, ICommerceApiSettings } from '@msdyn365-commerce/core';

/**
 * AddCustomCheckoutAdditionalInfo Input Action
 */
export class AddCustomCheckoutAdditionalInfoInput implements IAddCustomCheckoutAdditionalInfoData {
    // TODO: Construct the input needed to run the action
    public readonly apiSettings: ICommerceApiSettings;
    public transactionId?: string;
    public channelId?: string;
    public dataAreaId?: string;
    public poNumber?: string;
    public shippingDetailsForWarehouse?: string;
    public shippingDetailsForTransportation?: string;
    public messageToCustomerService?: string;
    public userAccountNumber?: string;
    constructor(
        apiSettings: ICommerceApiSettings,
        transactionId?: string,
        channelId?: string,
        dataAreaId?: string,
        poNumber?: string,
        shippingDetailsForWarehouse?: string,
        shippingDetailsForTransportation?: string,
        messageToCustomerService?: string,
        userAccountNumber?: string
    ) {
        this.apiSettings = apiSettings;
        this.transactionId = transactionId;
        this.channelId = channelId;
        this.dataAreaId = dataAreaId;
        this.poNumber = poNumber;
        this.shippingDetailsForWarehouse = shippingDetailsForWarehouse;
        this.shippingDetailsForTransportation = shippingDetailsForTransportation;
        this.messageToCustomerService = messageToCustomerService;
        this.userAccountNumber = userAccountNumber;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `DEFAULT`;
    public getCacheObjectType = () => 'Get Custom Checkout Additional Info';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IAddCustomCheckoutAdditionalInfoData extends Msdyn365.IActionInput {
    transactionId?: string;
    channelId?: string;
    dataAreaId?: string;
    poNumber?: string;
    shippingDetailsForWarehouse?: string;
    shippingDetailsForTransportation?: string;
    messageToCustomerService?: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365.ICreateActionContext): IAddCustomCheckoutAdditionalInfoData => {
    const { requestContext } = args;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create address input.  User is not authenticated.');
    }

    return new AddCustomCheckoutAdditionalInfoInput(args.requestContext.apiSettings);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function addCustomAdditionalInfo(input: AddCustomCheckoutAdditionalInfoInput, ctx: Msdyn365.IActionContext): Promise<any> {
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;

    // TODO: Uncomment the below line to get the value from a service
    const { apiSettings } = ctx.requestContext;

    const requestOptions: IHTTPRequestOptions = {
        headers: {
            oun: apiSettings.oun,
            'Content-Type': 'application/json'
        }
    };

    if (requestOptions.headers && ctx.requestContext.user.token) {
        requestOptions.headers.Authorization = `id_token ${ctx.requestContext.user.token}`;
    }

    const requestUrl = `${apiSettings.baseUrl}commerce/SendMoreOrderDetails`;

    const requestBody = {
        TransactionID: input.transactionId,
        ChannelId: input.channelId || '',
        DataAreaId: input.dataAreaId || '',
        PONumber: input.poNumber || '',
        WareHouseShippingDetails: input.shippingDetailsForWarehouse || '',
        TransportationShippingDetails: input.shippingDetailsForTransportation || '',
        MessageToCustomerService: input.messageToCustomerService || ''
    };

    return sendCommerceRequest<any>(requestUrl, 'post', requestBody, requestOptions)
        .then((response: IHTTPResponse) => {
            if (response.status >= 200 && response.status < 300 && response.data) {
                return response.data;
            }
            ctx.trace('[addCustomCheckoutAdditionalInfoAction] Invalid response recieved from RetailServer');
            return false;
        })
        .catch((error: IHTTPError) => {
            console.log('\n\n\n\n\n\n error', error, '\n\n\n\n\n\n end');
            ctx.trace(error.message);
            ctx.trace(error.stack || '');
            ctx.trace(`Unable to Fetch Custom Details.`);
            ctx.telemetry.exception(error);
            return <any>{};
        });
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IAddCustomCheckoutAdditionalInfoData>>addCustomAdditionalInfo,
    // TODO: Give your data action a unique id
    id: 'AddCustomCheckoutAdditionalInfo',
    input: <(args: Msdyn365.ICreateActionContext) => Msdyn365.IActionInput>createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
